
import { butter } from "../plugins/buttercms";
import DeliveryWeekMixin from "~/mixin/delivery-week-mixin";
import ProductMapMixin from "~/mixin/product-map-mixin";
import ProductFetchMixin from "~/mixin/product-fetch-mixin";
import Product from "~/types/Product";
import ShopifyProduct from "~/types/shopify/ShopifyProduct";
import CMSProduct from "~/types/cms/CMSProduct";
import { FilterBundleLandingProducts, filterProductByHandle, invokeShopifyQuery } from "~/utils/shopify";

export default {
  name: "cal-other-available",
  props: {
    selectedProductSKU: {
      type: String,
      required: true
    },
    heading: {
      required: false,
      type: String,
      default: 'others_available_heading'
    },
    paragraph: {
      required: false,
      type: String,
      default: ''
    },
  },
  mixins: [DeliveryWeekMixin, ProductMapMixin, ProductFetchMixin],
  data() {
    return {
        dateCode: "",
        deliveryTown: {},
        productList: [] as Product[],
        shopifyProduct: {} as ShopifyProduct,
    };
  },
  async created(){
    const region = sessionStorage.getItem("deliveryTown");
    const deliveryDate = sessionStorage.getItem("deliveryDate");
    if(region && deliveryDate) {
      this.deliveryTown = JSON.parse(region)["name"];
      this.dateCode = this.selectWeekNumber(deliveryDate);
    }

    this.getProductByHandle();
  },
  methods: {
    async fetchProductDataList() {
      this.productList = [];

      (this.dateCode === "") ? this.dateCode = this.selectWeekNumber(this.$dayjs().format("YYYY-MM-DD")) : "";

      let productData: CMSProduct[] = await this.getProductDataFromCMS();
      if (this.shopifyProduct.productType === "Flowers") {
        await this.getFlowersList(productData);
      } else if (this.shopifyProduct.productType === "Bundle") {
        await this.getGiftsetList(productData);
      } else if (this.shopifyProduct.productType === "Curated_Bundle") {
        await this.getCBCList(productData);
      } else if (this.shopifyProduct.productType?.toLowerCase().includes('subscription')) {
        await this.getSubscriptionList();
      }
    },
    async getProductDataFromCMS() {
      try {
        const products = await butter(this.$config).content.retrieve(["products"],
          {
            locale: this.$i18n.locale,
            timeout: 10000,
          }
        );
        return  products?.data.data.products;
      } catch (error) {
        console.error(error);
      }
    },
    async getFlowersList(productData: CMSProduct[]) {
      try {
        let centrepiecesList: Product[] = [];
        let driedFlowersList: Product[] = [];
        let flowersList: Product[] = [];

        await this.getLandingBouquets(this.dateCode);

        sessionStorage.productList = "";
        this.bouquetResponse.forEach((element: ShopifyProduct) => {
          let handle = element?.handle;
          if (handle) {
            sessionStorage.productList += handle.substring(0, handle.indexOf("_")) + " ";
          }
          let product = new Product(element, productData);

          if (product.tags.includes("centrepiece")) {
            centrepiecesList.push(product);
          } else if (product.tags.includes("dried-bouquet")) {
            driedFlowersList.push(product)
          } else {
            flowersList.push(product)
          }
        });
        let isCentrepiece = false;
        if (this.shopifyProduct.tags.includes("centrepiece")) {
          isCentrepiece = true;
          this.productList = centrepiecesList;
        } else if (this.shopifyProduct.tags.includes("dried-bouquet")) {
          driedFlowersList = this.sortProductsByStyle(driedFlowersList);
          this.productList = driedFlowersList;
        } else {
          flowersList = this.sortProductsByStyle(flowersList);
          this.productList = flowersList;
        }

        this.productList = this.productList.filter(flower => { return this.compareHandles(flower.handle, this.shopifyProduct.handle) });
        if (this.productList.length > 4) {
          this.productList = this.sortProductsByLowerWeekCode(this.productList).slice(0,4);
        }
        //if product is centrepiece, combine with flowers
        if (isCentrepiece) {
          this.productList = this.productList.concat(flowersList).slice(0,4);
        }
      } catch (error) {
        console.error(error)
      }
    },
    async getGiftsetList(productData: CMSProduct[]) {
      try {
        let giftsetsList: Product[] = [];

        await this.getLandingGiftsets(this.dateCode)
        this.giftsetResponse.forEach((element) => {
          giftsetsList.push(new Product(element, productData));
        });

        giftsetsList.sort((a, b) => b.price - a.price);
        this.productList = giftsetsList.filter(giftset => { return this.compareHandles(giftset.handle, this.shopifyProduct.handle) });
        if (this.productList.length > 4) {
          this.productList = this.sortProductsByLowerWeekCode(this.productList).slice(0,4);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getCBCList(productData: CMSProduct[]) {
      try {
        let curatedList: Product[] = [];

        const cbc = await invokeShopifyQuery(FilterBundleLandingProducts(this.dateCode, "Curated_Bundle"));
        let curatedListShopify: ShopifyProduct[] = this.mapShopifyProducts(cbc.data);
        const curatedListByTitle: { [key: string]: ShopifyProduct } = {};

        for (let item of curatedListShopify) {
          if (!(curatedListByTitle[item.title] && item.handle?.includes("_ca"))) {
            curatedListByTitle[item.title] = item;
          }
        }
        curatedListShopify = Object.values(curatedListByTitle);

        curatedListShopify.forEach((element) => {
          curatedList.push(new Product(element, productData));
        });

        curatedList.sort((a: { price: number }, b: { price: number }) => b.price - a.price);
        this.productList = curatedList.filter(curated => { return this.compareHandles(curated.handle, this.shopifyProduct.handle) });
        if (this.productList.length > 4) {
          this.productList = this.sortProductsByLowerWeekCode(this.productList).slice(0,4);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async getSubscriptionList() {
      try {
        let subscriptionList: Product[] = [];
        const prods = await butter(this.$config).content.retrieve(["subscriptions"], {
          locale: this.$i18n.locale,
          timeout: 10000
        });

        let cmsProductData: CMSProduct[] = prods?.data.data.subscriptions;
        const locale = this.$i18n.locale;
        const countryCode = locale.split("-")[1];

        await this.getSubscriptions({queryId: "products_with_product_type_multi_week", region: countryCode});

        this.subscriptionResponse.forEach((element: ShopifyProduct) => {
          subscriptionList.push(new Product(element, cmsProductData));
        });

        subscriptionList.sort((a: any, b: any) => a.title.localeCompare(b.title));
        this.productList = subscriptionList.filter(subscription => { return subscription.title !== this.shopifyProduct.title; });
      } catch (error) {
        console.error(error);
      }
    },
    compareHandles(otherProductHandle: any, selectedProductHandle: any){
      if(selectedProductHandle && selectedProductHandle.includes('_')){
        return otherProductHandle !== selectedProductHandle.split('_')[0];
      }
      return otherProductHandle !== selectedProductHandle;
    },
    async getProductByHandle() {
      const payload = await invokeShopifyQuery(filterProductByHandle(this.selectedProductSKU));
      this.shopifyProduct = new ShopifyProduct(payload.data.product ? payload.data.product : payload.data.node);

      await this.fetchProductDataList();
    },
    // sort product list according to the zero index value in tags
    sortProductsByLowerWeekCode(arrayOfObjects: any) {
      arrayOfObjects.sort((a: any, b: any) => a.tags[0] - b.tags[0]);
      return arrayOfObjects;
    }
  },
};
